/**
 * The leave request form.
 *
 * Two things this screen must get right, both about telling the user the truth BEFORE
 * they submit:
 *
 *  1. THE DAY COUNT IS SERVER-CALCULATED. `POST /leaves/preview` is called on every date
 *     change. Weekends, public holidays, half-day rules and the employee's own work week
 *     are policy, resolved per person at runtime. A client-side count would be a second
 *     implementation of the leave calendar and would quietly be wrong for anyone on a
 *     non-standard schedule.
 *
 *  2. THE BALANCE AFTER is shown inline. "You will have 7.5 days left" prevents the
 *     single most common support ticket — a request submitted, approved, and then found
 *     to overdraw a balance nobody displayed.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, Clock3 } from 'lucide-react';

import { isApiError } from '@frontend/api/client';
import {
  useLeavePreview,
  useLeaveTypes,
  useMyLeave,
  useSubmitLeave,
  type PreviewInput,
} from '@frontend/api/hooks/leave';
import { formatDate, humanise, todayISO } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { Checkbox, FieldShell, Input, Select, Textarea } from '@frontend/components/ui/Field';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

export function LeaveRequestPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const types = useLeaveTypes();
  const myLeave = useMyLeave();
  const submit = useSubmitLeave();

  const [leaveType, setLeaveType] = useState('');
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState('MORNING');
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const previewInput = useMemo<PreviewInput | null>(() => {
    if (leaveType === '' || fromDate === '' || toDate === '') return null;
    return {
      leaveType,
      fromDate,
      toDate,
      isHalfDay,
      halfDayPeriod: isHalfDay ? halfDayPeriod : null,
    };
  }, [leaveType, fromDate, toDate, isHalfDay, halfDayPeriod]);

  const preview = useLeavePreview(previewInput);
  const selectedType = types.data?.types.find((type) => type.value === leaveType);

  if (types.isLoading) return <LoadingRows rows={6} columns={2} />;
  if (types.isError) {
    return (
      <>
        <PageHeader title="Request leave" />
        <ErrorState
          error={types.error}
          onRetry={() => {
            void types.refetch();
          }}
        />
      </>
    );
  }

  const eligible = types.data?.eligible !== false;
  const datesInvalid = fromDate !== '' && toDate !== '' && fromDate > toDate;

  function handleSubmit(): void {
    const errors: Record<string, string> = {};
    if (leaveType === '') errors['leaveType'] = 'Choose the type of leave you are requesting.';
    if (fromDate === '') errors['fromDate'] = 'Choose the first day of leave.';
    if (toDate === '') errors['toDate'] = 'Choose the last day of leave.';
    if (datesInvalid) {
      errors['toDate'] = `The last day must be on or after the first day (first day: ${formatDate(fromDate)}).`;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    submit.mutate(
      {
        leaveType,
        fromDate,
        toDate,
        reason: reason.trim() === '' ? null : reason.trim(),
        isHalfDay,
        halfDayPeriod: isHalfDay ? halfDayPeriod : null,
      },
      {
        onSuccess: () => {
          toast.success('Leave requested', 'Your approver has been notified. You can withdraw it until they decide.');
          navigate('/leave');
        },
        onError: (error) => {
          if (isApiError(error)) {
            const mapped = error.fieldErrors();
            setFieldErrors(Object.keys(mapped).length > 0 ? mapped : {});
          }
          toast.fromError(error, 'Your leave request was not submitted');
        },
      },
    );
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Request leave"
        description="The day count and remaining balance below are calculated by the server using your own work week and the company holiday calendar."
        breadcrumbs={[{ label: 'Leave', to: '/leave' }, { label: 'Request' }]}
      />

      {!eligible && (
        <Banner tone="warning" className="mb-4" title="You are not currently eligible for paid leave">
          Your employment type does not accrue leave under the current policy, so this request may be
          refused. Speak to HR before submitting.
        </Banner>
      )}

      <div className="surface-card p-4">
        <div className="grid grid-cols-1 gap-field tablet:grid-cols-2">
          <FieldShell
            label="Leave type"
            htmlFor="leave-type"
            required
            fieldKey="leaveType"
            error={fieldErrors['leaveType'] ?? null}
            className="tablet:col-span-2"
            hint={
              selectedType?.unlimited === true
                ? 'This leave type has no annual limit.'
                : selectedType?.daysPerYear !== undefined
                  ? `${selectedType.daysPerYear} days per year under the current policy.`
                  : undefined
            }
          >
            {/* Options come from the leave_type LOOKUP, so a type HR adds appears here. */}
            <Select
              id="leave-type"
              name="leaveType"
              value={leaveType}
              onChange={(event) => setLeaveType(event.target.value)}
              data-testid="input-leaveType"
            >
              <option value="">Select…</option>
              {(types.data?.types ?? []).map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </FieldShell>

          <FieldShell
            label="First day"
            htmlFor="from-date"
            required
            fieldKey="fromDate"
            error={fieldErrors['fromDate'] ?? null}
          >
            <Input
              id="from-date"
              name="fromDate"
              type="date"
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value);
                if (toDate < event.target.value) setToDate(event.target.value);
              }}
              data-testid="input-fromDate"
            />
          </FieldShell>

          <FieldShell
            label="Last day"
            htmlFor="to-date"
            required
            fieldKey="toDate"
            error={fieldErrors['toDate'] ?? null}
          >
            <Input
              id="to-date"
              name="toDate"
              type="date"
              min={fromDate}
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              data-testid="input-toDate"
            />
          </FieldShell>

          {selectedType?.allowHalfDay === true && fromDate === toDate && (
            <div className="tablet:col-span-2">
              <Checkbox
                label="Half day only"
                checked={isHalfDay}
                onChange={(event) => setIsHalfDay(event.target.checked)}
                data-testid="input-isHalfDay"
              />
              {isHalfDay && (
                <div className="mt-2 max-w-[220px]">
                  <Select
                    name="halfDayPeriod"
                    value={halfDayPeriod}
                    onChange={(event) => setHalfDayPeriod(event.target.value)}
                    aria-label="Half day period"
                    data-testid="input-halfDayPeriod"
                  >
                    <option value="MORNING">Morning</option>
                    <option value="AFTERNOON">Afternoon</option>
                  </Select>
                </div>
              )}
            </div>
          )}

          <FieldShell
            label="Reason"
            htmlFor="leave-reason"
            fieldKey="reason"
            className="tablet:col-span-2"
            hint="Optional, but it helps your approver decide quickly — especially for short-notice requests."
          >
            <Textarea
              id="leave-reason"
              name="reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              data-testid="input-reason"
            />
          </FieldShell>
        </div>

        {/* ── Live preview ────────────────────────────────────────────── */}
        <PreviewPanel
          isLoading={preview.isLoading && previewInput !== null}
          error={preview.error}
          data={preview.data}
          leaveType={leaveType}
          minNoticeDays={types.data?.minNoticeDays}
          fromDate={fromDate}
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => navigate('/leave')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={submit.isPending}
            disabled={leaveType === '' || datesInvalid}
            data-testid="submit-leave"
          >
            Submit request
          </Button>
        </div>
      </div>

      {myLeave.data !== undefined && myLeave.data.balances.length > 0 && (
        <p className="mt-3 text-body text-content-secondary">
          Current balances:{' '}
          {myLeave.data.balances
            .map((balance) => `${humanise(balance.leaveType)} ${balance.availableDays}`)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

function PreviewPanel({
  isLoading,
  error,
  data,
  leaveType,
  minNoticeDays,
  fromDate,
}: {
  isLoading: boolean;
  error: unknown;
  data: ReturnType<typeof useLeavePreview>['data'];
  leaveType: string;
  minNoticeDays: number | undefined;
  fromDate: string;
}): JSX.Element | null {
  if (leaveType === '') return null;

  if (isLoading) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-md border border-line bg-surface-subtle px-3 py-2.5 text-row text-content-secondary">
        <Clock3 aria-hidden className="h-4 w-4 animate-spin" />
        Working out how many days this uses…
      </div>
    );
  }

  if (error !== null && error !== undefined) {
    // A preview failure is usually a real policy refusal (blackout period, notice rule),
    // and the server's message says which. Show it as-is.
    return (
      <Banner tone="error" className="mt-4" title="This request cannot be made as entered">
        {isApiError(error) ? error.message : 'The day count could not be calculated. Check the dates.'}
      </Banner>
    );
  }

  if (data === undefined) return null;

  const after = data.afterDays === null ? null : Number(data.afterDays);
  const wouldOverdraw = after !== null && after < 0;
  const noticeDays =
    minNoticeDays === undefined
      ? null
      : Math.round((new Date(fromDate).getTime() - Date.now()) / 86_400_000);
  const shortNotice = noticeDays !== null && minNoticeDays !== undefined && noticeDays < minNoticeDays;

  return (
    <div className="mt-4 space-y-2">
      <div
        className="rounded-md border border-info-border bg-info-bg px-3 py-2.5"
        data-testid="balance-preview"
      >
        <div className="flex items-start gap-2.5">
          <CalendarCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <div>
            <p className="text-row font-semibold text-content">
              {data.days} working day{data.days === '1.00' || data.days === '1' ? '' : 's'}
              <span className="ml-1 font-normal text-content-secondary">
                ({data.calendarDays} calendar day{data.calendarDays === 1 ? '' : 's'})
              </span>
            </p>
            {data.unlimited ? (
              <p className="mt-0.5 text-row text-content">
                This leave type is unlimited, so it does not draw down a balance.
              </p>
            ) : (
              <p className="mt-0.5 text-row text-content">
                You have {data.availableDays} days available now, and would have{' '}
                <strong className="tabular">{data.afterDays ?? '—'}</strong> after this request.
              </p>
            )}
            {data.needsSecondApproval && (
              <p className="mt-1 text-body text-content-secondary">
                A request this long needs a second approval after your manager.
              </p>
            )}
          </div>
        </div>
      </div>

      {wouldOverdraw && (
        <Banner tone="warning" title="This would take you into a negative balance">
          You are asking for {data.days} days but only have {data.availableDays} available. Request fewer
          days, choose a different leave type, or check with HR first — an approver is likely to refuse it.
        </Banner>
      )}

      {shortNotice && (
        <Banner tone="warning" title="Short notice">
          This leave type normally needs {minNoticeDays} days notice and you are giving {noticeDays}. Add a
          reason so your approver understands why.
        </Banner>
      )}
    </div>
  );
}
